import React, {useEffect, useState} from 'react';
import {Column} from '../types';
import {operatorRequiresValue} from '../utils/filterUtils';
import {SelectModal} from './SelectModal';
import {getRowDisplayValue} from '../utils/screenDisplayValue';
import {IconPlus} from '@tabler/icons-react';

export type FilterCondition = {
    type: 'condition';
    columnId: string;
    operator: string;
    value: string | string[];
};

const RELATION_TYPES = new Set(['many-to-one', 'one-to-many', 'user', 'created-by', 'updated-by']);
const MULTI_PICK_OPERATORS = new Set(['in', 'not_in']);
const PICK_OPERATORS = new Set(['eq', 'neq', 'in', 'not_in']);
// A relation holds a foreign key id picked from a list, so ordering and text
// operators only invite typing raw ids.
const RELATION_OPERATORS = new Set(['eq', 'neq', 'in', 'not_in', 'null', 'not_null']);

const N_DAYS_OPERATORS = new Set(['last_n_days', 'next_n_days', 'older_than_n_days', 'newer_than_n_days']);

/**
 * The value box follows the column: a date column gets a date picker, so what
 * reaches the API is ISO and never "24.08.2026" (which Postgres refuses),
 * a number column a numeric keypad, and the "N days" operators a day count
 * whatever the column. A list operator stays free text: several values,
 * comma-separated.
 */
const valueInputFor = (col: Column | undefined, operator: string): { type: string; inputMode?: 'numeric' | 'decimal'; placeholder: string } => {
    if (N_DAYS_OPERATORS.has(operator)) {
        return { type: 'number', inputMode: 'numeric', placeholder: 'Počet dní' };
    }
    if (MULTI_PICK_OPERATORS.has(operator)) {
        return { type: 'text', placeholder: 'Hodnoty oddelené čiarkou' };
    }
    switch (col?.columnType) {
        case 'date':
            return { type: 'date', placeholder: '' };
        case 'timestamp':
            return { type: 'datetime-local', placeholder: '' };
        case 'integer':
            return { type: 'number', inputMode: 'numeric', placeholder: 'Hodnota...' };
        case 'float':
            return { type: 'number', inputMode: 'decimal', placeholder: 'Hodnota...' };
        default:
            return { type: 'text', placeholder: 'Hodnota...' };
    }
};

const isRelationType = (col: Column | undefined): boolean =>
    !!col && RELATION_TYPES.has(col.columnType);

const isRelationColumn = (col: Column | undefined): boolean =>
    isRelationType(col) && !!col?.relatedScreen;

export type FilterGroup = {
    type: 'group';
    operator: 'AND' | 'OR';
    conditions: (FilterCondition | FilterGroup)[];
};

export interface FilterGroupProps {
    group: FilterGroup;
    columns: Column[];
    onChange: (group: FilterGroup) => void;
    onRemove?: () => void;
    depth?: number;
    operatorsByColumn?: Record<string, { value: string; label: string }[]>;
}

export const FilterGroupComponent: React.FC<FilterGroupProps> = ({
                                                                     group,
                                                                     columns,
                                                                     onChange,
                                                                     onRemove,
                                                                     depth = 0,
                                                                     operatorsByColumn = {}
                                                                 }) => {
    const getOperatorsForColumn = (columnId: string) => {
        // Fallback for a column type the operator list did not cover; names
        // must be the API's own, anything else is dropped there without a word.
        const operators = operatorsByColumn[columnId] || [
            {value: 'eq', label: 'Rovná sa'},
            {value: 'neq', label: 'Nerovná sa'},
            {value: 'like', label: 'Obsahuje'},
            {value: 'not_like', label: 'Neobsahuje'}
        ];
        const column = columns.find(c => c.id === columnId);
        return isRelationType(column)
            ? operators.filter(op => RELATION_OPERATORS.has(op.value))
            : operators;
    };

    const addCondition = () => {
        const firstColumn = columns[0];
        const columnId = firstColumn?.id || '';
        // Pick the first available operator for the column (e.g. boolean
        // columns don't have `eq` so falling back to it would be invalid).
        const firstOperator = getOperatorsForColumn(columnId)[0]?.value ?? 'eq';
        const newCondition: FilterCondition = {
            type: 'condition',
            columnId,
            operator: firstOperator,
            value: ''
        };
        onChange({
            ...group,
            conditions: [...group.conditions, newCondition]
        });
    };

    const addGroup = () => {
        const newGroup: FilterGroup = {
            type: 'group',
            operator: 'AND',
            conditions: []
        };
        onChange({
            ...group,
            conditions: [...group.conditions, newGroup]
        });
    };

    const updateCondition = (index: number, updated: FilterCondition | FilterGroup) => {
        const newConditions = [...group.conditions];
        newConditions[index] = updated;
        onChange({
            ...group,
            conditions: newConditions
        });
    };

    const removeCondition = (index: number) => {
        onChange({
            ...group,
            conditions: group.conditions.filter((_, i) => i !== index)
        });
    };

    const toggleOperator = () => {
        onChange({
            ...group,
            operator: group.operator === 'AND' ? 'OR' : 'AND'
        });
    };

    return (
        <div
            className="border rounded p-3 mb-2"
            style={{
                backgroundColor: depth % 2 === 0 ? '#f8f9fa' : '#ffffff',
                marginLeft: depth > 0 ? '20px' : '0'
            }}
        >
            <div className="d-flex align-items-center gap-2 mb-3">
                {group.conditions.length > 1 && (
                    <div>
                        <span className="text-muted small me-2">Kde</span>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={toggleOperator}
                            style={{minWidth: '60px'}}
                        >
                            {group.operator === 'AND' ? 'A' : 'ALEBO'}
                        </button>
                    </div>
                )}
                {onRemove && (
                    <button
                        className="btn btn-sm btn-ghost-danger ms-auto"
                        onClick={onRemove}
                        title="Odstrániť skupinu"
                    >
                        ×
                    </button>
                )}
            </div>

            {group.conditions.map((condition, index) => (
                <div key={index}>
                    {condition.type === 'condition' ? (
                        <div className="mb-2">
                            {index > 0 && group.conditions.length > 1 && (
                                <span className="badge bg-secondary text-white mb-2 d-block d-md-none"
                                      style={{minWidth: '45px'}}>{group.operator === 'AND' ? 'A' : 'ALEBO'}</span>
                            )}
                            <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
                                {index > 0 && group.conditions.length > 1 && (
                                    <span className="badge bg-secondary text-white d-none d-md-inline-block align-self-center"
                                          style={{minWidth: '45px'}}>{group.operator === 'AND' ? 'A' : 'ALEBO'}</span>
                                )}
                                <select
                                    className="form-select form-select-sm"
                                    style={{flex: '1 1 0', minWidth: 0}}
                                    value={condition.columnId}
                                    onChange={(e) => {
                                        const columnId = e.target.value;
                                        // Operators depend on the column type. Keeping one the new
                                        // column doesn't offer left the select painting its first
                                        // option while the state held the old operator - so the row
                                        // read "Rovná sa" with no picker beside it.
                                        const operators = getOperatorsForColumn(columnId);
                                        const operator = operators.some(op => op.value === condition.operator)
                                            ? condition.operator
                                            : (operators[0]?.value ?? 'eq');
                                        updateCondition(index, {
                                            ...condition,
                                            columnId,
                                            operator,
                                            value: ''
                                        });
                                    }}
                                >
                                    {columns.map(col => (
                                        <option key={col.id} value={col.id}>
                                            {col.displayName}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="form-select form-select-sm"
                                    style={{flex: '1 1 0', minWidth: 0}}
                                    value={condition.operator}
                                    onChange={(e) => {
                                        updateCondition(index, {
                                            ...condition,
                                            operator: e.target.value
                                        });
                                    }}
                                >
                                    {!getOperatorsForColumn(condition.columnId).some(op => op.value === condition.operator) && (
                                        <option value={condition.operator}>{condition.operator}</option>
                                    )}
                                    {getOperatorsForColumn(condition.columnId).map(op => (
                                        <option key={op.value} value={op.value}>
                                            {op.label}
                                        </option>
                                    ))}
                                </select>
                                {(() => {
                                    if (!operatorRequiresValue(condition.operator)) {
                                        return null;
                                    }
                                    const col = columns.find(c => c.id === condition.columnId);
                                    const relation = isRelationColumn(col) && PICK_OPERATORS.has(condition.operator);
                                    const multi = relation && MULTI_PICK_OPERATORS.has(condition.operator);
                                    // Multi picker → render on its own row below
                                    if (multi) return null;
                                    if (relation) {
                                        return (
                                            <div style={{flex: '1 1 0', minWidth: 0}}>
                                                <RelationValuePicker
                                                    column={col!}
                                                    operator={condition.operator}
                                                    value={condition.value}
                                                    onChange={(v) => updateCondition(index, { ...condition, value: v })}
                                                />
                                            </div>
                                        );
                                    }
                                    const input = valueInputFor(col, condition.operator);
                                    return (
                                        <input
                                            type={input.type}
                                            inputMode={input.inputMode}
                                            className="form-control form-control-sm"
                                            style={{flex: '1 1 0', minWidth: 0}}
                                            value={Array.isArray(condition.value) ? condition.value.join(',') : (condition.value ?? '')}
                                            onChange={(e) => {
                                                updateCondition(index, {
                                                    ...condition,
                                                    value: e.target.value
                                                });
                                            }}
                                            placeholder={input.placeholder}
                                        />
                                    );
                                })()}
                                {(() => {
                                    // For multi-pick the × moves to the picker row
                                    const col = columns.find(c => c.id === condition.columnId);
                                    const multi = isRelationColumn(col) && MULTI_PICK_OPERATORS.has(condition.operator);
                                    if (multi) return null;
                                    return (
                                        <button
                                            className="btn btn-sm btn-ghost-danger"
                                            onClick={() => removeCondition(index)}
                                            title="Odstrániť"
                                            style={{minWidth: '38px'}}
                                        >
                                            ×
                                        </button>
                                    );
                                })()}
                            </div>
                            {(() => {
                                // Multi-select picker rendered as its own row below; × button right-aligned with it.
                                const col = columns.find(c => c.id === condition.columnId);
                                if (!isRelationColumn(col)) return null;
                                if (!MULTI_PICK_OPERATORS.has(condition.operator)) return null;
                                return (
                                    <div className="mt-2 d-flex gap-2 align-items-center">
                                        <div style={{flex: '1 1 0', minWidth: 0}}>
                                            <RelationValuePicker
                                                column={col!}
                                                operator={condition.operator}
                                                value={condition.value}
                                                onChange={(v) => updateCondition(index, { ...condition, value: v })}
                                            />
                                        </div>
                                        <button
                                            className="btn btn-sm btn-ghost-danger"
                                            onClick={() => removeCondition(index)}
                                            title="Odstrániť"
                                            style={{minWidth: '38px'}}
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="mb-3">
                            {index > 0 && group.conditions.length > 1 && (
                                <span className="badge bg-secondary text-white mb-2 d-inline-block"
                                      style={{minWidth: '45px'}}>{group.operator}</span>
                            )}
                            <FilterGroupComponent
                                group={condition}
                                columns={columns}
                                onChange={(updated) => updateCondition(index, updated)}
                                onRemove={() => removeCondition(index)}
                                depth={depth + 1}
                                operatorsByColumn={operatorsByColumn}
                            />
                        </div>
                    )}
                </div>
            ))}

            <div className="d-flex flex-column flex-md-row gap-2 mt-3">
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={addCondition}
                >
                    + Pridať podmienku
                </button>
                {depth < 2 && (
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={addGroup}
                    >
                        + Pridať skupinu podmienok
                    </button>
                )}
            </div>
        </div>
    );
};

interface RelationValuePickerProps {
    column: Column;
    operator: string;
    value: string | string[];
    onChange: (v: string | string[]) => void;
}

const RelationValuePicker: React.FC<RelationValuePickerProps> = ({column, operator, value, onChange}) => {
    const [showModal, setShowModal] = useState(false);
    const [labels, setLabels] = useState<Record<string, string>>({});
    const isMulti = MULTI_PICK_OPERATORS.has(operator);

    const ids: string[] = Array.isArray(value)
        ? value.map(String).filter(v => v !== '')
        : (value !== '' && value != null ? [String(value)] : []);

    const screenId = column.relatedScreen?.id;
    const relatedColumns = column.relatedScreen?.columns;
    const idsKey = ids.join(',');

    // Fetch display labels for ids that aren't already labeled (e.g. when loading a saved filter).
    useEffect(() => {
        if (!screenId || ids.length === 0) return;
        const missing = ids.filter(id => !(id in labels));
        if (missing.length === 0) return;

        let cancelled = false;
        const params = new URLSearchParams();
        missing.forEach(id => params.append('ids[]', id));
        params.append('pageSize', String(missing.length));

        // Direct fetch with ids[] param since getScreenData helper doesn't expose it.
        fetch(`${process.env.REACT_APP_API_URL}/screens/${screenId}/data?${params.toString()}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('flowy_token') ?? ''}`,
                'Content-Type': 'application/json',
            },
        })
            .then(r => r.ok ? r.json() : null)
            .then(json => {
                if (cancelled || !json) return;
                const rows = Array.isArray(json.data) ? json.data : (json.data?.data ?? []);
                if (!Array.isArray(rows)) return;
                setLabels(prev => {
                    const next = {...prev};
                    rows.forEach((row: any) => {
                        const id = String(row?.id ?? '');
                        if (id) next[id] = getRowDisplayValue(row, relatedColumns) || `#${id}`;
                    });
                    return next;
                });
            })
            .catch(() => {});

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey, screenId]);

    const writeBack = (newIds: string[]) => {
        if (isMulti) {
            onChange(newIds);
        } else {
            onChange(newIds[0] ?? '');
        }
    };

    const handleSelect = (row: any, displayValue: string) => {
        const id = String(row?.id ?? '');
        if (!id) return;
        setLabels(prev => ({...prev, [id]: displayValue || `#${id}`}));
        if (isMulti) {
            if (!ids.includes(id)) writeBack([...ids, id]);
        } else {
            writeBack([id]);
        }
        setShowModal(false);
    };

    const removeId = (id: string) => writeBack(ids.filter(x => x !== id));

    if (!screenId) {
        return <div className="flex-fill" />;
    }

    const handleBoxClick = (e: React.MouseEvent) => {
        // ignore clicks on the X buttons
        if ((e.target as HTMLElement).closest('.relation-pick-remove')) return;
        setShowModal(true);
    };

    return (
        <div>
            <div
                className="form-select form-select-sm d-flex align-items-center gap-1"
                style={{
                    cursor: 'pointer',
                    backgroundImage: 'none',
                    paddingRight: '0.5rem',
                    height: '32px',
                    minHeight: '32px',
                    overflow: 'hidden',
                }}
                onClick={handleBoxClick}
                role="button"
                tabIndex={0}
            >
                {ids.length === 0 && (
                    <span className="text-muted small d-inline-flex align-items-center gap-1">
                        <IconPlus size={14}/> Vybrať...
                    </span>
                )}
                {ids.map(id => (
                    <span
                        key={id}
                        className="badge bg-secondary-subtle text-secondary-emphasis border d-inline-flex align-items-center gap-1 flex-shrink-0"
                        style={{fontWeight: 500, padding: '0.15em 0.45em', lineHeight: 1.3}}
                    >
                        {labels[id] ?? `#${id}`}
                        <span
                            className="relation-pick-remove"
                            role="button"
                            aria-label="Odstrániť"
                            onClick={(e) => { e.stopPropagation(); removeId(id); }}
                            style={{cursor: 'pointer', lineHeight: 1, fontSize: '0.95rem'}}
                        >
                            ×
                        </span>
                    </span>
                ))}
                {isMulti && ids.length > 0 && (
                    <IconPlus size={16} className="text-muted ms-auto" />
                )}
            </div>

            <SelectModal
                show={showModal}
                onHide={() => setShowModal(false)}
                screenId={screenId}
                onSelect={handleSelect}
            />
        </div>
    );
};
